import { useRef, useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { SHOWCASES } from "@/data/site";

const BeforeAfter = ({ before, after, altLabel, aspect }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const onUp = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [setFromClientX]);

  const start = (e) => {
    dragging.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(x);
  };

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      onMouseDown={start}
      onTouchStart={start}
      className={`relative w-full ${aspect || "aspect-[4/3] sm:aspect-[16/10]"} rounded-2xl overflow-hidden select-none cursor-ew-resize shadow-2xl`}
    >
      <img
        src={after}
        alt={`${altLabel} after transformation`}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={before}
          alt={`${altLabel} before transformation`}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>

      <span className="absolute top-4 left-4 bg-slate-900/70 text-white text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full pointer-events-none">
        Before
      </span>
      <span className="absolute top-4 right-4 bg-[#1656b8] text-white text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full pointer-events-none">
        After
      </span>

      <div
        className="absolute top-0 bottom-0 w-1 bg-white pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center">
          <MoveHorizontal size={20} className="text-[#1656b8]" />
        </div>
      </div>
    </div>
  );
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const ShowcaseRow = ({ item, index }) => {
  const reversed = index % 2 === 1;
  return (
    <div
      data-testid={`showcase-row-${index}`}
      className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
    >
      <motion.div
        initial={{ opacity: 0, x: reversed ? 30 : -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className={reversed ? "lg:order-2" : ""}
      >
        <span className="text-sm text-[#1656b8] uppercase tracking-[0.2em] font-semibold">
          {item.eyebrow}
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-3">
          {item.title}
        </h2>
        <p className="text-[#1656b8] font-semibold mt-2">{item.subtitle}</p>
        <p className="mt-5 text-slate-600 leading-relaxed">{item.blurb}</p>

        {item.gallery && item.gallery.length > 0 && (
          <div
            className="mt-8 grid grid-cols-4 gap-3"
            data-testid={`showcase-gallery-${index}`}
          >
            {item.gallery.map((src, i) => (
              <div
                key={i}
                className="aspect-square rounded-lg overflow-hidden border border-slate-200"
              >
                <img
                  src={src}
                  alt={`${item.altLabel} progress ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        )}

        <button
          data-testid={`showcase-quote-button-${index}`}
          onClick={() => scrollTo("contact")}
          className="mt-8 bg-[#1656b8] text-white rounded-lg px-8 py-4 font-semibold flex items-center justify-center gap-2 hover:bg-[#0f3d83] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
        >
          {item.cta}
        </button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: reversed ? -30 : 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className={reversed ? "lg:order-1" : ""}
      >
        <BeforeAfter before={item.before} after={item.after} altLabel={item.altLabel} aspect={item.aspect} />
        <p className="text-center text-sm text-slate-400 mt-4 flex items-center justify-center gap-2">
          <MoveHorizontal size={14} /> Drag the slider to reveal the transformation
        </p>
      </motion.div>
    </div>
  );
};

export const MirfieldShowcase = () => {
  return (
    <section
      id="latest-project"
      data-testid="mirfield-showcase"
      className="py-20 md:py-32 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20 lg:space-y-28">
        {SHOWCASES.map((item, i) => (
          <ShowcaseRow key={i} item={item} index={i} />
        ))}
      </div>
    </section>
  );
};
